<template>
    <v-row class="d-flex justify-center login-form-wrapper h-100 align-content-center align-items-center">
        <v-col class="px-xl-0" cols="12" lg="10" xl="7">
            <v-card v-if="loginForm" outlined class="login-screen">
                <v-card-text>
                    <div class="login-logo">
                        <img src="/images/logo/denzapine-logo-large.png" alt="denzapine-logo" />
                    </div>
                    <div class="login-title">
                        <h1>{{ t('login::title.log_in') }}</h1>
                    </div>
                    <div v-show="globalErrors.length">
                        <v-alert v-for="(error, index) in globalErrors" :key="index" type="error">{{ error }}</v-alert>
                    </div>

                    <v-form
                        ref="form"
                        v-model="isFormValid"
                        :loading="loading"
                        :rules="rules.title"
                        autocomplete="off"
                        lazy-validation
                    >
                        <v-text-field
                            ref="username"
                            v-model="item.username"
                            :error-messages="errorMessages.username"
                            :label="t('login::from.label.username')"
                            :placeholder="t('login::from.placeholder.username')"
                            :rules="rules.username"
                            outlined
                            required
                            rounded
                            class="medium"
                            @keyup.enter="postLogin"
                        ></v-text-field>
                        <v-text-field
                            ref="password"
                            v-model="item.password"
                            :error-messages="errorMessages.password"
                            :label="t('login::from.label.password')"
                            :placeholder="t('login::from.placeholder.password')"
                            :rules="rules.password"
                            :type="'password'"
                            outlined
                            rounded
                            required
                            class="medium"
                            @keyup.enter="postLogin"
                        ></v-text-field>
                        <div class="d-flex justify-content-between align-items-center mt-2 mb-10">
                            <div></div>
                            <div class="d-flex">
                                <a href @click.prevent="switchRecoveryForm">
                                    <u>{{ t('login::button.reset.password') }}</u>
                                </a>
                            </div>
                        </div>

                        <div class="d-flex flex-wrap justify-content-between align-items-center login-ctas-wrapper">
                            <div v-if="can('auth.register')" class="d-flex flex-row justify-content-end">
                                <a :href="registrationUrl" class="mr-3">{{ 'login::text.dont_have_account_yet' }}</a>
                                <v-btn color="primary" dark small @click="goToRegistration">
                                    {{ t('login::button.register') }}
                                </v-btn>
                            </div>
                        </div>
                        <v-btn :loading="loading" rounded block large color="primary" @click="postLogin">
                            {{ t('login::button.login') }}
                        </v-btn>
                    </v-form>
                </v-card-text>
            </v-card>
            <recovery-link-form
                v-if="recoveryForm"
                @cancel="switchLoginForm"
                @success="switchRecoveryComplete"
            ></recovery-link-form>
            <recovery-link-sent v-if="recoveryComplete" @cancel="switchLoginForm"></recovery-link-sent>
        </v-col>
        <v-col class="px-xl-0" cols="12" lg="10" xl="7">
            <div class="d-flex justify-content-center mt-10">
                <a style="font-size: 16px" href class="mx-2" @click="openDocumentsModal">
                    <u>Protocols</u>
                </a>
                <a style="font-size: 16px" href class="mx-2" @click="openNoticesModal">
                    <u>UK Notices</u>
                </a>
                <a style="font-size: 16px" href class="mx-2" @click="openInfoModal">
                    <u>Contact Details</u>
                </a>
            </div>
        </v-col>
        <v-dialog v-model="noticesModal" max-width="900">
            <v-card>
                <v-card-title>UK Notices</v-card-title>
                <v-card-text v-html="notices"></v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog v-model="contactInfoModal" max-width="900">
            <v-card>
                <v-card-title>Denzapine UK</v-card-title>
                <v-card-text v-html="contactInfo"></v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog v-model="documentsModal" max-width="900">
            <v-card>
                <v-card-title>Protocols</v-card-title>
                <v-card-text>
                    <div class="d-flex flex-column">
                        <div v-for="(document, index) in documents" :key="index" class="d-flex flex-column mb-3">
                            <div>
                                <span>{{ moment(document.effective_date).format('DD/MM/YYYY') }}: </span>
                                <strong>{{ document.title }}</strong>
                            </div>
                            <a :href="downloadUrl(document)" target="_blank"
                                >{{ document.filename }}
                                <v-icon size="small">mdi-download</v-icon>
                            </a>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import RecoveryLinkSent from './RecoveryLinkSent';

let formSubmitMixin = require(process.env.MIX_APP_ROOT +
    '/bgo/bgo-components/resources/js/mixins/FormSubmit.js').default;

export default {
    components: { RecoveryLinkSent },
    mixins: [formSubmitMixin],
    props: {
        postLoginRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            noticesModal: false,
            contactInfoModal: false,
            documentsModal: false,
            notices: [],
            contactInfo: [],
            documents: [],
            loginForm: true,
            loading: false,
            recoveryForm: false,
            recoveryComplete: false,
            item: {
                username: null,
                password: null,
                remember: null,
            },
            isFormValid: true,
            globalErrors: [],
            errorMessages: {
                username: [],
                password: [],
            },
            username: null,
            password: null,
        };
    },
    computed: {
        rules() {
            return {
                username: [(v) => !!v || t('default::validation.field.required')],
                password: [(v) => !!v || t('default::validation.field.required')],
            };
        },
        registrationUrl() {
            return route('auth.register');
        },
        landingUrl() {
            return route('landing');
        },
    },
    mounted() {
        this._repairAutocomplete();
        this.loadNoticesAndContactInfo();
        this.loadDocuments();
    },
    updated() {
        //this._repairAutocomplete()
    },
    methods: {
        downloadUrl(document) {
            return route('documents.download', document.file_id);
        },
        openDocumentsModal(event) {
            event.preventDefault();
            this.documentsModal = true;
        },
        openInfoModal(event) {
            event.preventDefault();
            this.contactInfoModal = true;
        },
        openNoticesModal(event) {
            event.preventDefault();
            this.noticesModal = true;
        },
        loadNoticesAndContactInfo() {
            axios.get(route('ssf.notices-and-info')).then((response) => {
                this.notices = response.data.notices;
                this.contactInfo = response.data.contact_info;
            });
        },
        loadDocuments() {
            axios.get(route('ssf.public-documents')).then((response) => {
                this.documents = response.data.data;
            });
        },
        switchLoginForm() {
            this.loginForm = true;
            this.recoveryForm = false;
            this.recoveryComplete = false;
            this.resetErrorMessages();
        },

        switchRecoveryForm() {
            this.loginForm = false;
            this.recoveryForm = true;
            this.recoveryComplete = false;
            this.resetErrorMessages();
        },

        switchRecoveryComplete() {
            this.loginForm = false;
            this.recoveryForm = false;
            this.recoveryComplete = true;
        },

        resetErrorMessages() {
            this.globalErrors = [];

            _.each(this.errorMessages, (messages, field) => {
                this.errorMessages[field] = [];
            });
        },

        postLogin() {
            this.submit(
                this.postLoginRoute,
                this.item,
                'post',
                (result) => {
                    window.location.href = result.data.message;
                },
                (error) => {
                    if (error.response && error.response.status !== 422) {
                        this.globalErrors = [error.response.data.error];
                    }
                }
            );
        },

        goToRegistration() {
            window.location.href = this.registrationUrl;
        },
        goToLanding() {
            window.location.href = this.landingUrl;
        },
        _repairAutocomplete() {
            const el = this.$el;
            el.querySelectorAll('input').forEach((it) => {
                switch (it.type) {
                    case 'username':
                        it.setAttribute('autocomplete', 'new-username');
                        break;
                    case 'password':
                        it.setAttribute('autocomplete', 'new-password');
                        break;
                    default:
                        break;
                }
            });
        },
    },
};
</script>
